import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import LicenseKeyInput from '../../components/shared/LicenseKeyInput';
import Badge from '../../components/ui/Badge';
import toast from 'react-hot-toast';

const STEPS = ['Enter Key', 'Verify', 'Activate'];
const STATUS_COLORS = { pending: 'yellow', active: 'green', expired: 'red', revoked: 'red', suspended: 'gray' };

export default function Activate() {
  const [step, setStep] = useState(0);
  const [licenseKey, setLicenseKey] = useState('');
  const [validationData, setValidationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleValidate = async () => {
    if (licenseKey.replace(/-/g, '').length !== 16) {
      setError('Please enter a complete license key');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/activate/validate', { licenseKey });
      setValidationData(data);
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    try {
      const payload = { applianceId: validationData.appliance.id };
      // Include certificateId for legacy flow
      if (validationData.certificate?.id && (!validationData.licenses || validationData.licenses.length === 0)) {
        payload.certificateId = validationData.certificate.id;
      }
      await api.post('/activate/confirm', payload);
      setStep(2);
      toast.success('Appliance activated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Activation failed');
    } finally {
      setLoading(false);
    }
  };

  const hasLicenses = validationData?.licenses && validationData.licenses.length > 0;

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] text-[#475569] mb-[2px]">Appliance Management</div>
        <h2 className="text-[22px] font-bold text-[#e2e8f0] tracking-[-0.3px]">Activate Appliance</h2>
        <p className="text-[13px] text-[#64748b] mt-[2px]">Enter your license key to activate your AIBOX</p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center justify-center gap-0 my-5 mb-7">
        {STEPS.map((label, i) => (
          <div key={i} className="flex items-center">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold border-[1.5px] transition-all ${
                i < step
                  ? 'bg-[#10b981] border-[#10b981] text-white'
                  : i === step
                  ? 'bg-[#06b6d4] border-[#06b6d4] text-[#060a10]'
                  : 'bg-[#0f1623] border-[rgba(148,163,184,0.16)] text-[#475569]'
              }`}>
                {i < step ? (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4L19 7"/>
                  </svg>
                ) : i + 1}
              </div>
              <span className={`text-[12px] font-medium ${i === step ? 'text-[#06b6d4]' : 'text-[#475569]'}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`w-[60px] h-[1.5px] mx-[6px] ${i < step ? 'bg-[#10b981]' : 'bg-[rgba(148,163,184,0.16)]'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 0: Enter key */}
      {step === 0 && (
        <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-xl p-7 max-w-[560px] mx-auto text-center">
          <h3 className="text-[16px] font-semibold text-[#e2e8f0] mb-1">Enter License Key</h3>
          <p className="text-[12.5px] text-[#64748b]">Find the key on your License Key Authorization card.</p>
          <div className="my-6">
            <LicenseKeyInput value={licenseKey} onChange={setLicenseKey} error={error} />
          </div>
          <button
            className="px-8 py-[10px] rounded-lg text-[12.5px] font-semibold bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7] transition-all disabled:opacity-50"
            onClick={handleValidate}
            disabled={loading}
          >
            {loading ? 'Validating...' : 'Validate Key'}
          </button>
        </div>
      )}

      {/* Step 1: Verify */}
      {step === 1 && validationData && (
        <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-xl p-7 max-w-[620px] mx-auto">
          <h3 className="text-[16px] font-semibold text-[#e2e8f0] mb-6">Verify Appliance Details</h3>

          <div className="space-y-4">
            {/* Organization */}
            <div>
              <p className="text-[10px] text-[#475569] uppercase tracking-wider">Organization</p>
              <p className="text-[13px] text-[#e2e8f0] mt-1">{validationData.organization.name}</p>
            </div>

            {/* Appliance details */}
            {validationData.appliance && (
              <div className="border-t border-[rgba(148,163,184,0.06)] pt-4">
                <p className="text-[10px] text-[#475569] uppercase tracking-wider mb-3">Appliance</p>
                <div className="bg-[#060a10] rounded-lg p-4 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] text-[#475569]">Unit Name</p>
                    <p className="text-[13px] font-medium text-[#e2e8f0]">{validationData.appliance.unitName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#475569]">Serial Number</p>
                    <p className="text-[13px] font-mono text-[#e2e8f0]">{validationData.appliance.serialNumber}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#475569]">Model</p>
                    <p className="text-[13px] text-[#e2e8f0] capitalize">{validationData.appliance.model?.replace('_', ' ')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* All 3 licenses */}
            {hasLicenses && (
              <div className="border-t border-[rgba(148,163,184,0.06)] pt-4">
                <p className="text-[10px] text-[#475569] uppercase tracking-wider mb-3">Licenses to Activate ({validationData.licenses.length})</p>
                <div className="space-y-2">
                  {validationData.licenses.map((lic) => (
                    <div key={lic.id} className="bg-[#060a10] rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-[12px] text-[#94a3b8] font-medium">{lic.softwareName}</p>
                        <p className="text-[13px] font-mono text-[#06b6d4] mt-0.5">{lic.licenseKey}</p>
                      </div>
                      <Badge color={STATUS_COLORS[lic.status] || 'gray'}>{lic.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy single-certificate view */}
            {!hasLicenses && validationData.certificate && (
              <div className="border-t border-[rgba(148,163,184,0.06)] pt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] text-[#475569] uppercase tracking-wider">License Key</p>
                  <p className="text-[13px] font-mono text-[#06b6d4] mt-1">{validationData.certificate.licenseKey}</p>
                </div>
                <div>
                  <p className="text-[10px] text-[#475569] uppercase tracking-wider">Software</p>
                  <p className="text-[13px] text-[#e2e8f0] mt-1">{validationData.certificate.softwareName}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-8">
            <button
              className="px-4 py-2 rounded-lg text-[12.5px] font-semibold bg-transparent border border-[rgba(148,163,184,0.10)] text-[#64748b] hover:border-[#06b6d4] hover:text-[#06b6d4] transition-all"
              onClick={() => { setStep(0); setValidationData(null); }}
            >
              Back
            </button>
            <button
              className="px-6 py-2 rounded-lg text-[12.5px] font-semibold bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7] transition-all disabled:opacity-50"
              onClick={handleActivate}
              disabled={loading}
            >
              {loading ? 'Activating...' : hasLicenses ? `Activate All ${validationData.licenses.length} Licenses` : 'Confirm & Activate'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Success */}
      {step === 2 && (
        <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-xl p-8 max-w-[560px] mx-auto text-center">
          <div className="w-16 h-16 bg-[rgba(16,185,129,0.1)] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#10b981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-[18px] font-semibold text-[#e2e8f0] mb-2">Activation Successful!</h3>
          <p className="text-[13px] text-[#64748b] mb-6">
            {hasLicenses
              ? `All ${validationData.licenses.length} licenses have been activated. Your appliance is ready to go online.`
              : 'Your AIBOX appliance has been activated and is ready to go online.'}
          </p>
          <button
            className="px-6 py-2 rounded-lg text-[12.5px] font-semibold bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7] transition-all"
            onClick={() => navigate('/central')}
          >
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  );
}
